import React, { Fragment, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  RefreshCw,
  Search,
  Workflow,
  X,
  Save,
  Loader2,
  ShieldCheck,
  Trash2,
  Pencil,
  LayoutDashboard,
} from "lucide-react";

const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  process.env.NEXT_PUBLIC_API_URL ||
  "https://api.schoolpay.tech";

const PBT_API = {
  template: "/pbt/template",
  templateDetail: (workflowUuid) => `/pbt/template/${workflowUuid}`,
};

const COLORS = {
  navy: "#062E63",
  blue: "#0B4DA2",
  bg: "#F3F6FA",
  surface: "#FFFFFF",
  border: "#E2E8F0",
  text: "#0F172A",
  muted: "#64748B",
  soft: "#94A3B8",
  red: "#DC2626",
  orange: "#F97316",
  green: "#059669",
  purple: "#7C3AED",
};

const MANDATORY_SELFIE_STEP = {
  key: "selfie_check_in",
  type: "photo",
  label: "Ambil Gambar Diri",
  description:
    "Pegawai wajib ambil gambar diri sendiri dahulu sebelum meneruskan langkah seterusnya.",
  required: 1,
  require_location: 1,
  require_photo: 1,
  require_remark: 0,
  require_approval: 0,
  assigned_role: "officer",
  locked: true,
  fields: [],
};

const STEP_TYPES = [
  { label: "Isi Borang", value: "form" },
  { label: "Check-In dengan Gambar + GPS", value: "location" },
  { label: "Gambar Bukti + GPS", value: "photo" },
  { label: "Pengesahan Penyelia", value: "approval" },
  { label: "Muat Naik Dokumen", value: "document" },
  { label: "Senarai Semak", value: "checklist" },
  { label: "Tanda Selesai", value: "completion" },
];

const FIELD_TYPES = [
  { label: "Teks Ringkas", value: "text" },
  { label: "Teks Panjang", value: "textarea" },
  { label: "Nombor", value: "number" },
  { label: "Tarikh", value: "date" },
  { label: "Masa", value: "time" },
  { label: "Tarikh & Masa", value: "datetime" },
  { label: "Pilihan Dropdown", value: "select" },
  { label: "Pilihan Berganda", value: "multi_select" },
  { label: "Ya / Tidak", value: "boolean" },
  { label: "Gambar", value: "photo" },
  { label: "Fail", value: "file" },
  { label: "Lokasi", value: "location" },
  { label: "Tandatangan", value: "signature" },
];

function getToken() {
  return localStorage.getItem("token") || "";
}

function slugifyCode(value, fallback = "PBT_TEMPLATE") {
  const code = String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

  return code || fallback;
}

function slugifyKey(value, fallback = "step") {
  const key = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

  return key || fallback;
}

async function apiRequest(path, options = {}) {
  const token = getToken();

  const res = await fetch(`${API_BASE_URL}${path}`, {
    method: options.method || "GET",
    headers: {
      "Content-Type": "application/json",
      token,
      ...(options.headers || {}),
    },
    body: options.body ? JSON.stringify(options.body) : undefined,
  });

  const json = await res.json().catch(() => ({}));

  if (!res.ok) {
    throw new Error(json?.message || `Permintaan gagal: ${res.status}`);
  }

  if (json?.response_code && json.response_code !== 1000) {
    throw new Error(json?.message || "Permintaan gagal");
  }

  return json;
}

function getStatusStyle(status) {
  const s = String(status || "").toUpperCase();

  if (s === "ACTIVE" || s === "IN_PROGRESS") {
    return {
      background: "#ECFDF5",
      color: "#065F46",
      borderColor: "#A7F3D0",
    };
  }

  if (s === "INACTIVE") {
    return {
      background: "#F8FAFC",
      color: "#475569",
      borderColor: "#E2E8F0",
    };
  }

  return {
    background: "#EFF6FF",
    color: "#1E3A8A",
    borderColor: "#BFDBFE",
  };
}

function isMandatorySelfieStep(step) {
  return (
    step?.key === "selfie_check_in" ||
    String(step?.label || "").trim().toLowerCase() === "ambil gambar diri"
  );
}

function prepareEditableSteps(flowSteps) {
  if (!Array.isArray(flowSteps)) return [];
  return flowSteps.filter((step) => !isMandatorySelfieStep(step));
}

const emptyStep = () => ({
  key: `step_${Date.now()}`,
  type: "form",
  label: "",
  description: "",
  required: 1,
  require_photo: 0,
  require_location: 0,
  require_remark: 0,
  require_approval: 0,
  assigned_role: "officer",
  fields: [],
});

const emptyField = () => ({
  key: `field_${Date.now()}`,
  label: "",
  type: "text",
  required: 0,
  placeholder: "",
  options: [],
});

function Badge({ children, style }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        borderRadius: 999,
        padding: "5px 9px",
        fontSize: 11,
        fontWeight: 900,
        border: "1px solid",
        whiteSpace: "nowrap",
        ...style,
      }}
    >
      {children}
    </span>
  );
}

function StatCard({ title, value, subtitle, icon, color }) {
  return (
    <div
      style={{
        background: COLORS.surface,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 22,
        padding: 18,
        boxShadow: "0 12px 30px rgba(15,23,42,0.05)",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
        <div>
          <div
            style={{
              fontSize: 13,
              color: COLORS.muted,
              fontWeight: 800,
              marginBottom: 8,
            }}
          >
            {title}
          </div>

          <div
            style={{
              fontSize: 30,
              lineHeight: "34px",
              color: COLORS.text,
              fontWeight: 950,
            }}
          >
            {value || 0}
          </div>

          <div
            style={{
              marginTop: 7,
              fontSize: 12,
              color: COLORS.soft,
              fontWeight: 700,
            }}
          >
            {subtitle}
          </div>
        </div>

        <div
          style={{
            width: 46,
            height: 46,
            borderRadius: 16,
            background: `${color}12`,
            color,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {icon}
        </div>
      </div>
    </div>
  );
}

function ModalShell({ title, subtitle, open, onClose, children }) {
  if (!open) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(15,23,42,0.55)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: 20,
      }}
      onMouseDown={onClose}
    >
      <div
        style={{
          width: "min(1080px, 96vw)",
          maxHeight: "90vh",
          overflow: "auto",
          background: "#FFFFFF",
          borderRadius: 24,
          boxShadow: "0 24px 70px rgba(15,23,42,0.28)",
        }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div
          style={{
            padding: 20,
            background: `linear-gradient(135deg, ${COLORS.navy}, ${COLORS.blue})`,
            color: "#FFFFFF",
            borderTopLeftRadius: 24,
            borderTopRightRadius: 24,
            display: "flex",
            justifyContent: "space-between",
            gap: 16,
          }}
        >
          <div>
            <div style={{ fontSize: 20, fontWeight: 950 }}>{title}</div>
            <div
              style={{
                marginTop: 5,
                color: "rgba(255,255,255,0.75)",
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              {subtitle}
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            style={{
              width: 38,
              height: 38,
              borderRadius: 12,
              border: "1px solid rgba(255,255,255,0.2)",
              background: "rgba(255,255,255,0.12)",
              color: "#FFFFFF",
              cursor: "pointer",
            }}
          >
            <X size={19} />
          </button>
        </div>

        <div style={{ padding: 20 }}>{children}</div>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, placeholder, type = "text", readOnly = false }) {
  return (
    <label style={{ display: "block" }}>
      <div style={{ fontSize: 12, fontWeight: 900, color: COLORS.text, marginBottom: 7 }}>
        {label}
      </div>

      <input
        type={type}
        value={value || ""}
        onChange={(e) => onChange?.(e.target.value)}
        placeholder={placeholder}
        readOnly={readOnly}
        style={{
          width: "100%",
          height: 42,
          borderRadius: 13,
          border: `1px solid ${COLORS.border}`,
          outline: "none",
          padding: "0 12px",
          fontSize: 14,
          fontWeight: 650,
          color: COLORS.text,
          boxSizing: "border-box",
          background: readOnly ? "#F8FAFC" : "#FFFFFF",
        }}
      />
    </label>
  );
}

function Select({ label, value, onChange, options }) {
  return (
    <label style={{ display: "block" }}>
      <div style={{ fontSize: 12, fontWeight: 900, color: COLORS.text, marginBottom: 7 }}>
        {label}
      </div>

      <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: "100%",
          height: 42,
          borderRadius: 13,
          border: `1px solid ${COLORS.border}`,
          outline: "none",
          padding: "0 12px",
          fontSize: 14,
          fontWeight: 650,
          color: COLORS.text,
          boxSizing: "border-box",
          background: "#FFFFFF",
        }}
      >
        {options.map((item) => (
          <option key={item.value || item} value={item.value || item}>
            {item.label || item}
          </option>
        ))}
      </select>
    </label>
  );
}

function Textarea({ label, value, onChange, placeholder, rows = 4 }) {
  return (
    <label style={{ display: "block" }}>
      <div style={{ fontSize: 12, fontWeight: 900, color: COLORS.text, marginBottom: 7 }}>
        {label}
      </div>

      <textarea
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        style={{
          width: "100%",
          borderRadius: 13,
          border: `1px solid ${COLORS.border}`,
          outline: "none",
          padding: 12,
          fontSize: 14,
          fontWeight: 650,
          color: COLORS.text,
          boxSizing: "border-box",
          background: "#FFFFFF",
          resize: "vertical",
        }}
      />
    </label>
  );
}

function PrimaryButton({ children, onClick, disabled, icon }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      style={{
        border: 0,
        borderRadius: 14,
        padding: "11px 15px",
        background: disabled
          ? "#CBD5E1"
          : `linear-gradient(135deg, ${COLORS.navy}, ${COLORS.blue})`,
        color: "#FFFFFF",
        fontWeight: 900,
        fontSize: 13,
        cursor: disabled ? "not-allowed" : "pointer",
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
      }}
    >
      {icon}
      {children}
    </button>
  );
}

function SecondaryButton({ children, onClick, icon }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        border: `1px solid ${COLORS.border}`,
        borderRadius: 14,
        padding: "10px 14px",
        background: "#FFFFFF",
        color: COLORS.text,
        fontWeight: 900,
        fontSize: 13,
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
      }}
    >
      {icon}
      {children}
    </button>
  );
}

export default function PbtWorkflowDashboard() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [search, setSearch] = useState("");
  const [showWorkflowModal, setShowWorkflowModal] = useState(false);
  const [editingWorkflowUuid, setEditingWorkflowUuid] = useState(null);
  const [loadingTemplateDetail, setLoadingTemplateDetail] = useState(false);

  const [workflowForm, setWorkflowForm] = useState({
    name: "",
    description: "",
    category: "PBT_TEMPLATE",
    icon: "clipboard-text-outline",
    color: "#0B4DA2",
    flow_mode: "multi_step",
    is_active: 1,
    flow_steps: [],
  });

  const generatedProcessCode = useMemo(
    () => slugifyCode(workflowForm.name, "PBT_TEMPLATE"),
    [workflowForm.name]
  );

  const metrics = useMemo(
    () => ({
      templates: templates.length || 0,
      activeTemplates: templates.filter((item) => Number(item.is_active) === 1).length,
      inactiveTemplates: templates.filter((item) => Number(item.is_active) !== 1).length,
    }),
    [templates]
  );

  const filteredTemplates = useMemo(() => {
    const q = search.trim().toLowerCase();

    if (!q) return templates;

    return templates.filter((item) =>
      [item.name, item.code, item.description, item.category]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [templates, search]);

  const resetWorkflowForm = () => {
    setWorkflowForm({
      name: "",
      description: "",
      category: "PBT_TEMPLATE",
      icon: "clipboard-text-outline",
      color: "#0B4DA2",
      flow_mode: "multi_step",
      is_active: 1,
      flow_steps: [],
    });
  };

  const loadDashboard = async () => {
    setLoading(true);

    try {
      const templateRes = await apiRequest(PBT_API.template);
      setTemplates(Array.isArray(templateRes?.data) ? templateRes.data : []);
    } catch (err) {
      alert(err.message || "Gagal memuatkan template");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreateTemplateModal = () => {
    setEditingWorkflowUuid(null);
    resetWorkflowForm();
    setShowWorkflowModal(true);
  };

  const openEditTemplateModal = async (workflowUuid) => {
    if (!workflowUuid) return;

    setEditingWorkflowUuid(workflowUuid);
    setShowWorkflowModal(true);
    setLoadingTemplateDetail(true);

    try {
      const json = await apiRequest(PBT_API.templateDetail(workflowUuid));
      const record = json?.data || {};

      setWorkflowForm({
        name: record.name || "",
        description: record.description || "",
        category: record.category || "PBT_TEMPLATE",
        icon: record.icon || "clipboard-text-outline",
        color: record.color || "#0B4DA2",
        flow_mode: record.flow_mode || "multi_step",
        is_active: Number(record.is_active ?? 1),
        flow_steps: prepareEditableSteps(record.flow_steps || []),
      });
    } catch (err) {
      alert(err.message || "Gagal memuatkan template");
      setShowWorkflowModal(false);
      setEditingWorkflowUuid(null);
    } finally {
      setLoadingTemplateDetail(false);
    }
  };

  const closeTemplateModal = () => {
    setShowWorkflowModal(false);
    setEditingWorkflowUuid(null);
    setLoadingTemplateDetail(false);
    resetWorkflowForm();
  };

  const updateWorkflowStep = (stepIndex, patch) => {
    setWorkflowForm((prev) => {
      const nextSteps = [...prev.flow_steps];
      nextSteps[stepIndex] = { ...nextSteps[stepIndex], ...patch };
      return { ...prev, flow_steps: nextSteps };
    });
  };

  const addWorkflowStep = () => {
    setWorkflowForm((prev) => ({
      ...prev,
      flow_steps: [...prev.flow_steps, emptyStep()],
    }));
  };

  const removeWorkflowStep = (stepIndex) => {
    setWorkflowForm((prev) => ({
      ...prev,
      flow_steps: prev.flow_steps.filter((_, index) => index !== stepIndex),
    }));
  };

  const addStepField = (stepIndex) => {
    setWorkflowForm((prev) => {
      const nextSteps = [...prev.flow_steps];
      const step = nextSteps[stepIndex];

      nextSteps[stepIndex] = {
        ...step,
        fields: [...(step.fields || []), emptyField()],
      };

      return { ...prev, flow_steps: nextSteps };
    });
  };

  const updateStepField = (stepIndex, fieldIndex, patch) => {
    setWorkflowForm((prev) => {
      const nextSteps = [...prev.flow_steps];
      const step = nextSteps[stepIndex];
      const nextFields = [...(step.fields || [])];

      nextFields[fieldIndex] = {
        ...nextFields[fieldIndex],
        ...patch,
      };

      nextSteps[stepIndex] = {
        ...step,
        fields: nextFields,
      };

      return { ...prev, flow_steps: nextSteps };
    });
  };

  const removeStepField = (stepIndex, fieldIndex) => {
    setWorkflowForm((prev) => {
      const nextSteps = [...prev.flow_steps];
      const step = nextSteps[stepIndex];

      nextSteps[stepIndex] = {
        ...step,
        fields: (step.fields || []).filter((_, index) => index !== fieldIndex),
      };

      return { ...prev, flow_steps: nextSteps };
    });
  };

  const addFieldOption = (stepIndex, fieldIndex) => {
    setWorkflowForm((prev) => {
      const nextSteps = [...prev.flow_steps];
      const nextFields = [...(nextSteps[stepIndex].fields || [])];
      const field = nextFields[fieldIndex];

      nextFields[fieldIndex] = {
        ...field,
        options: [...(field.options || []), { label: "", value: "" }],
      };

      nextSteps[stepIndex] = {
        ...nextSteps[stepIndex],
        fields: nextFields,
      };

      return { ...prev, flow_steps: nextSteps };
    });
  };

  const updateFieldOption = (stepIndex, fieldIndex, optionIndex, patch) => {
    setWorkflowForm((prev) => {
      const nextSteps = [...prev.flow_steps];
      const nextFields = [...(nextSteps[stepIndex].fields || [])];
      const field = nextFields[fieldIndex];
      const nextOptions = [...(field.options || [])];

      nextOptions[optionIndex] = {
        ...nextOptions[optionIndex],
        ...patch,
      };

      nextFields[fieldIndex] = {
        ...field,
        options: nextOptions,
      };

      nextSteps[stepIndex] = {
        ...nextSteps[stepIndex],
        fields: nextFields,
      };

      return { ...prev, flow_steps: nextSteps };
    });
  };

  const removeFieldOption = (stepIndex, fieldIndex, optionIndex) => {
    setWorkflowForm((prev) => {
      const nextSteps = [...prev.flow_steps];
      const nextFields = [...(nextSteps[stepIndex].fields || [])];
      const field = nextFields[fieldIndex];

      nextFields[fieldIndex] = {
        ...field,
        options: (field.options || []).filter((_, index) => index !== optionIndex),
      };

      nextSteps[stepIndex] = {
        ...nextSteps[stepIndex],
        fields: nextFields,
      };

      return { ...prev, flow_steps: nextSteps };
    });
  };

  const saveWorkflow = async () => {
    setSaving(true);

    try {
      if (!workflowForm.name.trim()) {
        throw new Error("Nama template diperlukan");
      }

      const allSteps = [MANDATORY_SELFIE_STEP, ...workflowForm.flow_steps];

      const normalizedSteps = allSteps.map((step, index) => {
        if (!step.label?.trim()) {
          throw new Error(`Nama langkah ${index + 1} diperlukan`);
        }

        const stepType = step.type || "form";
        const stepKey = slugifyKey(step.key || step.label, `step_${index + 1}`);

        const isPhotoGpsStep =
          stepType === "location" ||
          stepType === "photo" ||
          stepKey === "selfie_check_in";

        return {
          key: stepKey,
          type: stepType,
          label: step.label,
          description: step.description || "",
          required: Number(step.required || 0),
          require_photo: isPhotoGpsStep ? 1 : Number(step.require_photo || 0),
          require_location: isPhotoGpsStep ? 1 : Number(step.require_location || 0),
          require_remark: Number(step.require_remark || 0),
          require_approval: Number(step.require_approval || 0),
          assigned_role: stepType === "approval" ? "supervisor" : "officer",
          fields: (step.fields || []).map((field, fieldIndex) => {
            if (!field.label?.trim()) {
              throw new Error(
                `Nama medan ${fieldIndex + 1} diperlukan dalam langkah ${index + 1}`
              );
            }

            return {
              key: slugifyKey(field.key || field.label, `field_${fieldIndex + 1}`),
              label: field.label,
              type: field.type || "text",
              required: Number(field.required || 0),
              placeholder: field.placeholder || "",
              options:
                field.type === "select" || field.type === "multi_select"
                  ? (field.options || []).filter((option) => option.label && option.value)
                  : undefined,
            };
          }),
        };
      });

      const payload = {
        name: workflowForm.name,
        description: workflowForm.description,
        category: workflowForm.category || "PBT_TEMPLATE",
        icon: workflowForm.icon || "clipboard-text-outline",
        color: workflowForm.color || "#0B4DA2",
        flow_mode: workflowForm.flow_mode,
        flow_steps: normalizedSteps,
        is_active: Number(workflowForm.is_active || 1),
      };

      if (editingWorkflowUuid) {
        await apiRequest(PBT_API.templateDetail(editingWorkflowUuid), {
          method: "PUT",
          body: payload,
        });
      } else {
        await apiRequest(PBT_API.template, {
          method: "POST",
          body: {
            ...payload,
            code: generatedProcessCode,
          },
        });
      }

      closeTemplateModal();
      await loadDashboard();
    } catch (err) {
      alert(err.message || "Gagal menyimpan template");
    } finally {
      setSaving(false);
    }
  };

  const deleteWorkflowTemplate = async (workflowUuid, workflowName) => {
    const confirmed = window.confirm(
      `Padam template "${workflowName || "ini"}"?\n\nTemplate ini akan disembunyikan daripada senarai.`
    );

    if (!confirmed) return;

    try {
      await apiRequest(PBT_API.templateDetail(workflowUuid), {
        method: "DELETE",
      });

      await loadDashboard();
    } catch (err) {
      alert(err.message || "Gagal memadam template");
    }
  };

  const renderStepRequirements = (step) => {
    const tags = [];

    if (Number(step.required || 0) === 1) tags.push("Wajib");
    if (Number(step.require_location || 0) === 1) tags.push("GPS");
    if (Number(step.require_photo || 0) === 1) tags.push("Gambar");
    if (Number(step.require_remark || 0) === 1) tags.push("Catatan");
    if (Number(step.require_approval || 0) === 1) tags.push("Semakan");

    if (!tags.length) {
      tags.push("Tiada syarat khas");
    }

    return (
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 7 }}>
        {tags.map((tag) => (
          <span
            key={tag}
            style={{
              padding: "4px 7px",
              borderRadius: 999,
              background: "#FFFFFF",
              border: `1px solid ${COLORS.border}`,
              color: COLORS.muted,
              fontSize: 10,
              fontWeight: 900,
            }}
          >
            {tag}
          </span>
        ))}
      </div>
    );
  };

  const renderTemplates = () => {
    if (loading) {
      return (
        <div
          style={{
            padding: 44,
            textAlign: "center",
            color: COLORS.muted,
            fontWeight: 800,
          }}
        >
          <Loader2
            size={24}
            style={{
              animation: "spin 1s linear infinite",
              marginBottom: 8,
            }}
          />
          <div>Memuatkan template...</div>
        </div>
      );
    }

    if (!filteredTemplates.length) {
      return (
        <div style={{ padding: 44, textAlign: "center", color: COLORS.muted }}>
          <Workflow size={36} color={COLORS.soft} />
          <div style={{ marginTop: 10, fontSize: 16, fontWeight: 900 }}>
            Belum ada template
          </div>
          <div style={{ marginTop: 4, fontSize: 13, fontWeight: 650 }}>
            Cipta template pertama untuk rekod operasi PBT.
          </div>
        </div>
      );
    }

    return (
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
          gap: 14,
        }}
      >
        {filteredTemplates.map((item) => {
          const flowSteps = Array.isArray(item.flow_steps) ? item.flow_steps : [];

          return (
            <div
              key={item.uuid}
              style={{
                border: `1px solid ${COLORS.border}`,
                borderRadius: 20,
                padding: 16,
                background: "#FFFFFF",
                boxShadow: "0 10px 22px rgba(15,23,42,0.04)",
              }}
            >
              <div style={{ display: "flex", gap: 12 }}>
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 15,
                    background: `${item.color || COLORS.blue}14`,
                    color: item.color || COLORS.blue,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Workflow size={22} />
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 15,
                      fontWeight: 950,
                      color: COLORS.text,
                      lineHeight: "20px",
                      wordBreak: "break-word",
                      overflowWrap: "anywhere",
                      whiteSpace: "normal",
                      maxWidth: "100%",
                    }}
                  >
                    {item.name}
                  </div>

                  <div
                    style={{
                      marginTop: 3,
                      fontSize: 12,
                      color: COLORS.muted,
                      fontWeight: 700,
                      wordBreak: "break-word",
                      overflowWrap: "anywhere",
                    }}
                  >
                    {item.code} · {item.category || "PBT_TEMPLATE"}
                  </div>
                </div>
              </div>

              <div
                style={{
                  marginTop: 13,
                  fontSize: 13,
                  color: COLORS.muted,
                  fontWeight: 700,
                  lineHeight: "18px",
                  minHeight: 36,
                }}
              >
                {item.description || "Tiada penerangan"}
              </div>

              <div
                style={{
                  marginTop: 14,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 10,
                }}
              >
                <Badge style={getStatusStyle(item.is_active ? "ACTIVE" : "INACTIVE")}>
                  {item.is_active ? "Aktif" : "Tidak Aktif"}
                </Badge>

                <span style={{ fontSize: 12, color: COLORS.soft, fontWeight: 900 }}>
                  {flowSteps.length} langkah
                </span>
              </div>

              <div
                style={{
                  marginTop: 13,
                  borderTop: `1px solid ${COLORS.border}`,
                  paddingTop: 12,
                }}
              >
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 950,
                    color: COLORS.text,
                    marginBottom: 8,
                  }}
                >
                  Langkah Diperlukan
                </div>

                {flowSteps.length === 0 ? (
                  <div
                    style={{
                      padding: 10,
                      borderRadius: 12,
                      border: `1px dashed ${COLORS.border}`,
                      color: COLORS.muted,
                      fontSize: 12,
                      fontWeight: 800,
                      background: "#F8FAFC",
                    }}
                  >
                    Belum ada langkah disimpan.
                  </div>
                ) : (
                  <div style={{ display: "grid", gap: 8 }}>
                    {flowSteps.slice(0, 5).map((step, index) => (
                      <div
                        key={`${step.key}-${index}`}
                        style={{
                          padding: 9,
                          borderRadius: 12,
                          background: "#F8FAFC",
                          border: `1px solid ${COLORS.border}`,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 8,
                          }}
                        >
                          <div
                            style={{
                              width: 22,
                              height: 22,
                              borderRadius: 999,
                              background: COLORS.blue,
                              color: "#FFFFFF",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: 11,
                              fontWeight: 950,
                              flexShrink: 0,
                            }}
                          >
                            {index + 1}
                          </div>

                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div
                              style={{
                                fontSize: 12,
                                fontWeight: 950,
                                color: COLORS.text,
                                wordBreak: "break-word",
                              }}
                            >
                              {step.label || step.key || `Langkah ${index + 1}`}
                            </div>

                            <div
                              style={{
                                marginTop: 2,
                                fontSize: 11,
                                fontWeight: 700,
                                color: COLORS.muted,
                              }}
                            >
                              {step.type || "form"}
                            </div>

                            {renderStepRequirements(step)}
                          </div>
                        </div>
                      </div>
                    ))}

                    {flowSteps.length > 5 && (
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 900,
                          color: COLORS.soft,
                          paddingLeft: 2,
                        }}
                      >
                        +{flowSteps.length - 5} langkah lagi
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div
                style={{
                  marginTop: 12,
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 8,
                  flexWrap: "wrap",
                }}
              >
                <button
                  type="button"
                  onClick={() => openEditTemplateModal(item.uuid)}
                  style={smallNeutralButtonStyle}
                >
                  <Pencil size={13} />
                  Edit
                </button>

                <button
                  type="button"
                  onClick={() => deleteWorkflowTemplate(item.uuid, item.name)}
                  style={dangerSmallButtonStyle}
                >
                  <Trash2 size={13} />
                  Padam
                </button>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <Fragment>
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @media (max-width: 1100px) {
          .pbtStatsGrid {
            grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
          }
        }

        @media (max-width: 700px) {
          .pbtStatsGrid {
            grid-template-columns: 1fr !important;
          }

          .pbtMainWrap {
            padding: 12px !important;
          }

          .twoColGrid {
            grid-template-columns: 1fr !important;
          }

          .threeColGrid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>

      <div
        className="pbtMainWrap"
        style={{
          minHeight: "100vh",
          background: COLORS.bg,
          padding: 22,
          fontFamily:
            "Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        }}
      >
        <div style={{ maxWidth: 1480, margin: "0 auto" }}>
          <div
            style={{
              background: `linear-gradient(135deg, ${COLORS.navy}, ${COLORS.blue})`,
              borderRadius: 28,
              padding: 24,
              color: "#FFFFFF",
              boxShadow: "0 18px 40px rgba(6,46,99,0.22)",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                position: "absolute",
                right: -80,
                top: -90,
                width: 260,
                height: 260,
                borderRadius: 260,
                background: "rgba(255,255,255,0.08)",
              }}
            />

            <div
              style={{
                position: "relative",
                display: "flex",
                justifyContent: "space-between",
                gap: 20,
                alignItems: "flex-start",
                flexWrap: "wrap",
              }}
            >
              <div>
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "7px 10px",
                    borderRadius: 999,
                    background: "rgba(255,255,255,0.12)",
                    border: "1px solid rgba(255,255,255,0.18)",
                    fontSize: 12,
                    fontWeight: 900,
                    color: "#DBEAFE",
                    marginBottom: 12,
                  }}
                >
                  <ShieldCheck size={15} />
                  PBT OPERATIONS
                </div>

                <div
                  style={{
                    fontSize: 30,
                    fontWeight: 950,
                    letterSpacing: -0.8,
                  }}
                >
                  PBT Template Rekod
                </div>

                <div
                  style={{
                    marginTop: 8,
                    color: "rgba(255,255,255,0.75)",
                    fontSize: 14,
                    fontWeight: 650,
                    maxWidth: 760,
                    lineHeight: "22px",
                  }}
                >
                  Urus template rekod operasi PBT, langkah borang, gambar bukti,
                  lokasi GPS, catatan dan semakan pegawai.
                </div>
              </div>

              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <SecondaryButton
                  onClick={() => navigate("/pbt/cases")}
                  icon={<LayoutDashboard size={16} />}
                >
                  Dashboard Kes
                </SecondaryButton>

                <SecondaryButton onClick={loadDashboard} icon={<RefreshCw size={16} />}>
                  Muat Semula
                </SecondaryButton>

                <PrimaryButton onClick={openCreateTemplateModal} icon={<Plus size={16} />}>
                  Cipta Template
                </PrimaryButton>
              </div>
            </div>
          </div>

          <div
            className="pbtStatsGrid"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
              gap: 14,
              marginTop: 18,
            }}
          >
            <StatCard
              title="Jumlah Template"
              value={metrics.templates}
              subtitle="Semua template rekod"
              color={COLORS.purple}
              icon={<Workflow size={23} />}
            />

            <StatCard
              title="Template Aktif"
              value={metrics.activeTemplates}
              subtitle="Boleh digunakan"
              color={COLORS.green}
              icon={<ShieldCheck size={23} />}
            />

            <StatCard
              title="Tidak Aktif"
              value={metrics.inactiveTemplates}
              subtitle="Tidak dipaparkan kepada pegawai"
              color={COLORS.orange}
              icon={<Workflow size={23} />}
            />
          </div>

          <div
            style={{
              marginTop: 18,
              background: "#FFFFFF",
              borderRadius: 24,
              border: `1px solid ${COLORS.border}`,
              boxShadow: "0 12px 30px rgba(15,23,42,0.05)",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                padding: 16,
                borderBottom: `1px solid ${COLORS.border}`,
                display: "flex",
                justifyContent: "space-between",
                gap: 14,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <div>
                <div style={{ fontSize: 18, fontWeight: 950, color: COLORS.text }}>
                  Senarai Template
                </div>

                <div
                  style={{
                    marginTop: 3,
                    fontSize: 13,
                    fontWeight: 700,
                    color: COLORS.muted,
                  }}
                >
                  Template rekod yang dicipta untuk PBT ini.
                </div>
              </div>

              <div
                style={{
                  height: 40,
                  borderRadius: 13,
                  border: `1px solid ${COLORS.border}`,
                  padding: "0 11px",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  minWidth: 280,
                  background: "#FFFFFF",
                }}
              >
                <Search size={16} color={COLORS.soft} />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Cari template..."
                  style={{
                    border: 0,
                    outline: 0,
                    flex: 1,
                    fontWeight: 700,
                    color: COLORS.text,
                  }}
                />
              </div>
            </div>

            <div style={{ padding: 16 }}>{renderTemplates()}</div>
          </div>
        </div>
      </div>

      <ModalShell
        open={showWorkflowModal}
        onClose={closeTemplateModal}
        title={editingWorkflowUuid ? "Edit Template" : "Cipta Template"}
        subtitle={
          editingWorkflowUuid
            ? "Kemaskini maklumat template dan langkah rekod yang diperlukan."
            : "Tetapkan langkah rekod yang pegawai perlu ikut. Langkah pertama gambar diri dan GPS akan dimasukkan secara automatik."
        }
      >
        {loadingTemplateDetail ? (
          <div
            style={{
              padding: 40,
              textAlign: "center",
              color: COLORS.muted,
              fontWeight: 800,
            }}
          >
            <Loader2
              size={24}
              style={{
                animation: "spin 1s linear infinite",
                marginBottom: 8,
              }}
            />
            <div>Memuatkan template...</div>
          </div>
        ) : (
          <Fragment>
            <div style={{ display: "grid", gap: 18 }}>
              <div style={sectionStyle}>
                <div style={sectionHeaderStyle}>
                  <div style={sectionIconStyle}>
                    <Workflow size={20} />
                  </div>

                  <div>
                    <div style={sectionTitleStyle}>Maklumat Template</div>
                    <div style={sectionSubtitleStyle}>
                      Namakan template ini. Kod template akan dijana secara automatik.
                    </div>
                  </div>
                </div>

                <div
                  className="twoColGrid"
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 14,
                  }}
                >
                  <Input
                    label="Nama Template"
                    value={workflowForm.name}
                    onChange={(v) => setWorkflowForm({ ...workflowForm, name: v })}
                    placeholder="Contoh: Pemeriksaan Aduan Majlis"
                  />

                  <Input label="Kod Template" value={generatedProcessCode} readOnly />

                  <div>
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 900,
                        color: COLORS.text,
                        marginBottom: 7,
                      }}
                    >
                      Jenis Template
                    </div>

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 10,
                      }}
                    >
                      {[
                        {
                          label: "Beberapa Langkah",
                          value: "multi_step",
                          description: "Sesuai untuk rekod operasi yang ada banyak langkah.",
                        },
                        {
                          label: "Satu Langkah Sahaja",
                          value: "single_step",
                          description: "Sesuai untuk rekod ringkas dan cepat.",
                        },
                      ].map((option) => {
                        const active = workflowForm.flow_mode === option.value;

                        return (
                          <button
                            key={option.value}
                            type="button"
                            onClick={() =>
                              setWorkflowForm({
                                ...workflowForm,
                                flow_mode: option.value,
                              })
                            }
                            style={{
                              border: `1px solid ${active ? COLORS.blue : COLORS.border}`,
                              background: active ? "#EFF6FF" : "#FFFFFF",
                              borderRadius: 15,
                              padding: 12,
                              cursor: "pointer",
                              textAlign: "left",
                              boxShadow: active
                                ? "0 8px 18px rgba(11,77,162,0.12)"
                                : "none",
                            }}
                          >
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <span
                                style={{
                                  width: 16,
                                  height: 16,
                                  borderRadius: 999,
                                  border: `2px solid ${active ? COLORS.blue : COLORS.soft}`,
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  flexShrink: 0,
                                }}
                              >
                                {active && (
                                  <span
                                    style={{
                                      width: 7,
                                      height: 7,
                                      borderRadius: 999,
                                      background: COLORS.blue,
                                    }}
                                  />
                                )}
                              </span>

                              <span
                                style={{
                                  fontSize: 13,
                                  fontWeight: 950,
                                  color: active ? COLORS.blue : COLORS.text,
                                }}
                              >
                                {option.label}
                              </span>
                            </div>

                            <div
                              style={{
                                marginTop: 7,
                                fontSize: 12,
                                fontWeight: 700,
                                color: COLORS.muted,
                                lineHeight: "17px",
                              }}
                            >
                              {option.description}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div />

                  <div style={{ gridColumn: "1 / -1" }}>
                    <Textarea
                      label="Penerangan Ringkas"
                      value={workflowForm.description}
                      onChange={(v) =>
                        setWorkflowForm({
                          ...workflowForm,
                          description: v,
                        })
                      }
                      placeholder="Terangkan kegunaan template ini."
                      rows={3}
                    />
                  </div>
                </div>
              </div>

              <div style={{ ...sectionStyle, background: "#FFFFFF" }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 12,
                    marginBottom: 16,
                  }}
                >
                  <div>
                    <div style={sectionTitleStyle}>Langkah Rekod</div>
                    <div style={sectionSubtitleStyle}>
                      Tambah langkah yang perlu dilengkapkan oleh pegawai mengikut susunan.
                    </div>
                  </div>

                  <PrimaryButton onClick={addWorkflowStep} icon={<Plus size={16} />}>
                    Tambah Langkah
                  </PrimaryButton>
                </div>

                <div style={{ display: "grid", gap: 14 }}>
                  <div
                    style={{
                      border: `1px solid ${COLORS.border}`,
                      borderRadius: 18,
                      background: "#EFF6FF",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        padding: 14,
                        background: "#FFFFFF",
                        borderBottom: `1px solid ${COLORS.border}`,
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 12,
                        alignItems: "center",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div
                          style={{
                            width: 34,
                            height: 34,
                            borderRadius: 999,
                            background: COLORS.blue,
                            color: "#FFFFFF",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 13,
                            fontWeight: 950,
                          }}
                        >
                          1
                        </div>

                        <div>
                          <div style={{ fontSize: 14, fontWeight: 950, color: COLORS.text }}>
                            Ambil Gambar Diri
                          </div>

                          <div
                            style={{
                              marginTop: 2,
                              fontSize: 11,
                              color: COLORS.muted,
                              fontWeight: 700,
                            }}
                          >
                            Langkah wajib pertama untuk semua rekod operasi
                          </div>
                        </div>
                      </div>

                      <Badge style={getStatusStyle("ACTIVE")}>Wajib</Badge>
                    </div>

                    <div style={{ padding: 14 }}>
                      <div
                        style={{
                          padding: 12,
                          borderRadius: 14,
                          background: "#FFFFFF",
                          border: `1px solid ${COLORS.border}`,
                          color: COLORS.muted,
                          fontSize: 13,
                          fontWeight: 750,
                          lineHeight: "20px",
                        }}
                      >
                        Pegawai mesti ambil gambar diri sendiri dahulu. Sistem akan simpan
                        gambar dan lokasi GPS sebagai bukti kehadiran di lokasi operasi.
                        Selepas langkah ini selesai, pegawai boleh teruskan langkah seterusnya.
                      </div>
                    </div>
                  </div>

                  {workflowForm.flow_steps.length === 0 && (
                    <div
                      style={{
                        border: `2px dashed ${COLORS.border}`,
                        background: "#F8FAFC",
                        borderRadius: 18,
                        padding: 24,
                        textAlign: "center",
                        color: COLORS.muted,
                        fontSize: 14,
                        fontWeight: 750,
                      }}
                    >
                      Langkah pertama sudah ditetapkan sebagai{" "}
                      <strong>Ambil Gambar Diri</strong>. Tekan{" "}
                      <strong>Tambah Langkah</strong> untuk tambah langkah rekod seterusnya.
                    </div>
                  )}

                  {workflowForm.flow_steps.map((step, stepIndex) => (
                    <div
                      key={`${step.key}-${stepIndex}`}
                      style={{
                        border: `1px solid ${COLORS.border}`,
                        borderRadius: 18,
                        background: "#F8FAFC",
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          padding: 14,
                          background: "#FFFFFF",
                          borderBottom: `1px solid ${COLORS.border}`,
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 12,
                          alignItems: "center",
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <div
                            style={{
                              width: 34,
                              height: 34,
                              borderRadius: 999,
                              background: COLORS.blue,
                              color: "#FFFFFF",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: 13,
                              fontWeight: 950,
                            }}
                          >
                            {stepIndex + 2}
                          </div>

                          <div>
                            <div style={{ fontSize: 14, fontWeight: 950, color: COLORS.text }}>
                              {step.label || `Langkah ${stepIndex + 2}`}
                            </div>

                            <div
                              style={{
                                marginTop: 2,
                                fontSize: 11,
                                color: COLORS.muted,
                                fontWeight: 700,
                              }}
                            >
                              Langkah rekod {stepIndex + 2}
                            </div>
                          </div>
                        </div>

                        <button
                          type="button"
                          onClick={() => removeWorkflowStep(stepIndex)}
                          style={dangerSmallButtonStyle}
                        >
                          <Trash2 size={13} />
                          Buang
                        </button>
                      </div>

                      <div style={{ padding: 14 }}>
                        <div
                          className="twoColGrid"
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: 12,
                          }}
                        >
                          <Input
                            label="Nama Langkah"
                            value={step.label}
                            onChange={(v) =>
                              updateWorkflowStep(stepIndex, {
                                label: v,
                                key: slugifyKey(v, step.key),
                              })
                            }
                            placeholder="Contoh: Pergi ke Lokasi"
                          />

                          <Input label="Kod Langkah" value={step.key} readOnly />

                          <Select
                            label="Apa pegawai perlu buat?"
                            value={step.type}
                            onChange={(v) =>
                              updateWorkflowStep(stepIndex, {
                                type: v,
                                require_photo:
                                  v === "location" || v === "photo"
                                    ? 1
                                    : step.require_photo,
                                require_location:
                                  v === "location" || v === "photo"
                                    ? 1
                                    : step.require_location,
                                assigned_role: step.assigned_role || "officer",
                              })
                            }
                            options={STEP_TYPES}
                          />

                          <Input
                            label="Peranan Pegawai"
                            value={step.assigned_role}
                            onChange={(v) =>
                              updateWorkflowStep(stepIndex, {
                                assigned_role: v,
                              })
                            }
                            placeholder="officer / supervisor / admin"
                          />

                          <div style={{ gridColumn: "1 / -1" }}>
                            <Textarea
                              label="Arahan Untuk Pegawai"
                              value={step.description}
                              onChange={(v) =>
                                updateWorkflowStep(stepIndex, {
                                  description: v,
                                })
                              }
                              placeholder="Contoh: Pegawai perlu berada di lokasi sebelum meneruskan rekod."
                              rows={2}
                            />
                          </div>
                        </div>

                        <div style={innerBoxStyle}>
                          <div
                            style={{
                              fontSize: 13,
                              fontWeight: 950,
                              color: COLORS.text,
                              marginBottom: 10,
                            }}
                          >
                            Keperluan Langkah
                          </div>

                          <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                            {[
                              ["required", "Wajib Buat"],
                              ["require_location", "Perlu GPS"],
                              ["require_photo", "Perlu Gambar"],
                              ["require_remark", "Perlu Catatan"],
                              ["require_approval", "Perlu Semakan"],
                            ].map(([key, label]) => (
                              <label key={key} style={chipStyle(Number(step[key] || 0) === 1)}>
                                <input
                                  type="checkbox"
                                  checked={Number(step[key] || 0) === 1}
                                  onChange={(e) =>
                                    updateWorkflowStep(stepIndex, {
                                      [key]: e.target.checked ? 1 : 0,
                                    })
                                  }
                                />
                                {label}
                              </label>
                            ))}
                          </div>

                          {Number(step.require_photo || 0) === 1 &&
                            Number(step.require_location || 0) === 1 && (
                              <div
                                style={{
                                  marginTop: 10,
                                  padding: 10,
                                  borderRadius: 12,
                                  background: "#EAF2FF",
                                  color: "#1E3A8A",
                                  fontSize: 12,
                                  fontWeight: 750,
                                }}
                              >
                                GPS akan direkod bersama gambar yang dimuat naik.
                              </div>
                            )}
                        </div>

                        <div style={innerBoxStyle}>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              gap: 10,
                              marginBottom: 12,
                            }}
                          >
                            <div>
                              <div style={{ fontSize: 13, fontWeight: 950, color: COLORS.text }}>
                                Soalan / Medan
                              </div>

                              <div
                                style={{
                                  marginTop: 2,
                                  fontSize: 13,
                                  color: COLORS.muted,
                                  fontWeight: 700,
                                }}
                              >
                                Tambah maklumat yang perlu diisi oleh pegawai untuk langkah ini.
                              </div>
                            </div>

                            <SecondaryButton
                              onClick={() => addStepField(stepIndex)}
                              icon={<Plus size={15} />}
                            >
                              Tambah Medan
                            </SecondaryButton>
                          </div>

                          {(step.fields || []).length === 0 && (
                            <div
                              style={{
                                border: `1px dashed ${COLORS.border}`,
                                borderRadius: 14,
                                padding: 18,
                                textAlign: "center",
                                color: COLORS.muted,
                                fontSize: 13,
                                fontWeight: 700,
                              }}
                            >
                              Belum ada medan. Tekan Tambah Medan jika langkah ini perlukan input.
                            </div>
                          )}

                          <div style={{ display: "grid", gap: 10 }}>
                            {(step.fields || []).map((field, fieldIndex) => (
                              <div
                                key={`${field.key}-${fieldIndex}`}
                                style={{
                                  border: `1px solid ${COLORS.border}`,
                                  borderRadius: 15,
                                  padding: 12,
                                  background: "#F8FAFC",
                                }}
                              >
                                <div
                                  className="threeColGrid"
                                  style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 1fr 170px",
                                    gap: 10,
                                  }}
                                >
                                  <Input
                                    label="Nama Soalan"
                                    value={field.label}
                                    onChange={(v) =>
                                      updateStepField(stepIndex, fieldIndex, {
                                        label: v,
                                        key: slugifyKey(v, field.key),
                                      })
                                    }
                                    placeholder="Contoh: Tajuk Aduan"
                                  />

                                  <Input label="Kod Soalan" value={field.key} readOnly />

                                  <Select
                                    label="Jenis Jawapan"
                                    value={field.type}
                                    onChange={(v) =>
                                      updateStepField(stepIndex, fieldIndex, {
                                        type: v,
                                      })
                                    }
                                    options={FIELD_TYPES}
                                  />

                                  <Input
                                    label="Contoh Jawapan / Panduan"
                                    value={field.placeholder}
                                    onChange={(v) =>
                                      updateStepField(stepIndex, fieldIndex, {
                                        placeholder: v,
                                      })
                                    }
                                    placeholder="Contoh: Tulis catatan ringkas"
                                  />

                                  <label
                                    style={{
                                      display: "flex",
                                      alignItems: "flex-end",
                                      gap: 8,
                                      paddingBottom: 10,
                                      fontSize: 12,
                                      fontWeight: 900,
                                      color: COLORS.text,
                                      cursor: "pointer",
                                    }}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={Number(field.required || 0) === 1}
                                      onChange={(e) =>
                                        updateStepField(stepIndex, fieldIndex, {
                                          required: e.target.checked ? 1 : 0,
                                        })
                                      }
                                    />
                                    Wajib Isi
                                  </label>

                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "flex-end",
                                      justifyContent: "flex-end",
                                    }}
                                  >
                                    <button
                                      type="button"
                                      onClick={() => removeStepField(stepIndex, fieldIndex)}
                                      style={dangerSmallButtonStyle}
                                    >
                                      Buang Soalan
                                    </button>
                                  </div>
                                </div>

                                {(field.type === "select" || field.type === "multi_select") && (
                                  <div
                                    style={{
                                      marginTop: 12,
                                      paddingTop: 12,
                                      borderTop: `1px solid ${COLORS.border}`,
                                    }}
                                  >
                                    <div
                                      style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        marginBottom: 8,
                                      }}
                                    >
                                      <div
                                        style={{
                                          fontSize: 12,
                                          fontWeight: 950,
                                          color: COLORS.text,
                                        }}
                                      >
                                        Pilihan Jawapan
                                      </div>

                                      <button
                                        type="button"
                                        onClick={() => addFieldOption(stepIndex, fieldIndex)}
                                        style={smallNeutralButtonStyle}
                                      >
                                        + Tambah Pilihan
                                      </button>
                                    </div>

                                    <div style={{ display: "grid", gap: 8 }}>
                                      {(field.options || []).map((option, optionIndex) => (
                                        <div
                                          key={optionIndex}
                                          style={{
                                            display: "grid",
                                            gridTemplateColumns: "1fr 1fr 80px",
                                            gap: 8,
                                          }}
                                        >
                                          <input
                                            value={option.label || ""}
                                            onChange={(e) =>
                                              updateFieldOption(
                                                stepIndex,
                                                fieldIndex,
                                                optionIndex,
                                                { label: e.target.value }
                                              )
                                            }
                                            placeholder="Nama pilihan"
                                            style={miniInputStyle}
                                          />

                                          <input
                                            value={option.value || ""}
                                            onChange={(e) =>
                                              updateFieldOption(
                                                stepIndex,
                                                fieldIndex,
                                                optionIndex,
                                                { value: e.target.value }
                                              )
                                            }
                                            placeholder="Nilai sistem"
                                            style={miniInputStyle}
                                          />

                                          <button
                                            type="button"
                                            onClick={() =>
                                              removeFieldOption(
                                                stepIndex,
                                                fieldIndex,
                                                optionIndex
                                              )
                                            }
                                            style={dangerSmallButtonStyle}
                                          >
                                            Buang
                                          </button>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={addWorkflowStep}
                    style={{
                      border: `2px dashed ${COLORS.border}`,
                      background: "#F8FAFC",
                      borderRadius: 18,
                      padding: 18,
                      color: COLORS.blue,
                      fontWeight: 950,
                      fontSize: 14,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 8,
                    }}
                  >
                    <Plus size={18} />
                    Tambah Langkah
                  </button>
                </div>
              </div>
            </div>

            <div
              style={{
                marginTop: 18,
                display: "flex",
                justifyContent: "flex-end",
                gap: 10,
              }}
            >
              <SecondaryButton onClick={closeTemplateModal}>Batal</SecondaryButton>

              <PrimaryButton
                onClick={saveWorkflow}
                disabled={saving}
                icon={saving ? <Loader2 size={16} /> : <Save size={16} />}
              >
                {editingWorkflowUuid ? "Simpan Perubahan" : "Simpan Template"}
              </PrimaryButton>
            </div>
          </Fragment>
        )}
      </ModalShell>
    </Fragment>
  );
}

const sectionStyle = {
  border: `1px solid ${COLORS.border}`,
  borderRadius: 20,
  padding: 16,
  background: "#F8FAFC",
};

const sectionHeaderStyle = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  marginBottom: 16,
};

const sectionIconStyle = {
  width: 42,
  height: 42,
  borderRadius: 15,
  background: "#EAF2FF",
  color: COLORS.blue,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

const sectionTitleStyle = {
  fontSize: 15,
  fontWeight: 950,
  color: COLORS.text,
};

const sectionSubtitleStyle = {
  marginTop: 3,
  fontSize: 13,
  fontWeight: 700,
  color: COLORS.muted,
};

const innerBoxStyle = {
  marginTop: 14,
  padding: 13,
  borderRadius: 16,
  border: `1px solid ${COLORS.border}`,
  background: "#FFFFFF",
};

const chipStyle = (active) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 7,
  padding: "8px 10px",
  borderRadius: 999,
  border: `1px solid ${active ? "#BFDBFE" : COLORS.border}`,
  background: active ? "#EFF6FF" : "#FFFFFF",
  color: active ? COLORS.blue : COLORS.muted,
  fontSize: 12,
  fontWeight: 900,
  cursor: "pointer",
});

const dangerSmallButtonStyle = {
  border: "1px solid #FECACA",
  borderRadius: 12,
  padding: "8px 10px",
  background: "#FEF2F2",
  color: "#B91C1C",
  fontSize: 12,
  fontWeight: 900,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
};

const smallNeutralButtonStyle = {
  border: `1px solid ${COLORS.border}`,
  borderRadius: 10,
  padding: "7px 10px",
  background: "#FFFFFF",
  color: COLORS.text,
  fontSize: 12,
  fontWeight: 900,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
};

const miniInputStyle = {
  height: 36,
  borderRadius: 10,
  border: `1px solid ${COLORS.border}`,
  outline: "none",
  padding: "0 10px",
  fontSize: 13,
  fontWeight: 700,
  color: COLORS.text,
  background: "#FFFFFF",
  boxSizing: "border-box",
};